import { eq } from "drizzle-orm";
import { dining_table, type PowerSyncDrizzleDB, reservation } from "@comandero/powersync";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { generateId } from "@fleet/utils";

export type ReservationKind = "reservation" | "waitlist";
export type ReservationStatus = "reserved" | "seated" | "no_show" | "cancelled";

export interface CreateReservationOpts {
  tableId?: string | null;
  customerId?: string | null;
  phone?: string;
  partySize?: number;
  /** ISO-8601. NULL for waitlist (no committed time). */
  reservedAt?: string | null;
  kind?: ReservationKind;
  note?: string;
}

/** Creates reservation (or waitlist entry). CC-7: awaited write. */
export async function createReservationMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  branchId: string,
  customerName: string,
  opts: CreateReservationOpts = {},
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(reservation).values({
    id,
    organization_id: organizationId,
    branch_id: branchId,
    table_id: opts.tableId ?? null,
    customer_id: opts.customerId ?? null,
    customer_name: customerName.trim(),
    phone: opts.phone?.trim() || null,
    party_size: Math.max(1, Math.trunc(opts.partySize ?? 2)),
    reserved_at: opts.reservedAt ?? null,
    kind: opts.kind ?? "reservation",
    status: "reserved",
    note: opts.note?.trim() || null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Changes reservation status (reserved → seated/no_show/cancelled). */
export async function setReservationStatusMutation(
  db: PowerSyncDrizzleDB,
  reservationId: string,
  status: ReservationStatus,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, reservation, reservation.id, reservationId, { status }, options);
}

/**
 * Seats a reservation: marks it `seated` and, if table assigned, marks table
 * `occupied`. CC-7: ambas escrituras con await.
 */
export async function seatReservationMutation(
  db: PowerSyncDrizzleDB,
  reservationId: string,
  tableId?: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    reservation,
    reservation.id,
    reservationId,
    { status: "seated", ...(tableId ? { table_id: tableId } : {}) },
    options,
  );

  const current = await db
    .select({ table_id: reservation.table_id })
    .from(reservation)
    .where(eq(reservation.id, reservationId))
    .limit(1);
  const finalTableId = tableId ?? current[0]?.table_id ?? null;
  if (finalTableId) {
    await patchRowVerified(
      db,
      dining_table,
      dining_table.id,
      finalTableId,
      { status: "occupied" },
      options,
    );
  }
}

export async function deleteReservationMutation(
  db: PowerSyncDrizzleDB,
  reservationId: string,
): Promise<void> {
  await db.delete(reservation).where(eq(reservation.id, reservationId));
}
